/**
 * boardUrl — 게시판 URL 의 ID 인코딩/디코딩 유틸
 *
 * Supabase UUID 는 8-4-4-4-12 형식(`xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx`).
 * URL 에 그대로 노출되면 하이픈 4개가 시각적으로 지저분하므로,
 * 외부 노출 시에는 하이픈을 제거한 32자 hex 로 표시하고
 * 내부에서 DB 조회 시에는 다시 dashed 형태로 복원한다.
 *
 * 두 형식을 모두 lenient 하게 받아들여 기존 dashed URL 도 그대로 동작 (backwards compat).
 */

const UUID_HEX32 = /^[0-9a-fA-F]{32}$/;
const UUID_DASHED = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/** UUID 를 URL 표시용으로 인코딩 — dashed → hex32. 비-UUID 는 그대로 반환. */
export function encodeIdForUrl(id: string): string {
  if (!id) return id;
  if (UUID_DASHED.test(id)) return id.replace(/-/g, '');
  return id;
}

/** URL 세그먼트를 DB 조회용 UUID 로 디코딩 — hex32 → dashed. 비-UUID 또는 이미 dashed 면 그대로. */
export function decodeIdFromUrl(seg: string | null | undefined): string | null {
  if (!seg) return null;
  if (UUID_DASHED.test(seg)) return seg;
  if (UUID_HEX32.test(seg)) {
    return (
      seg.slice(0, 8) + '-' +
      seg.slice(8, 12) + '-' +
      seg.slice(12, 16) + '-' +
      seg.slice(16, 20) + '-' +
      seg.slice(20, 32)
    );
  }
  return seg; // 비-UUID 인 경우 (이론상 존재하지 않으나 방어용)
}

/** `/board/:boardId/:postId` 형태 URL 빌더 — 둘 다 인코딩. */
export function buildBoardUrl(boardId?: string | null, postId?: string | null): string {
  if (!boardId) return '/board';
  const b = encodeIdForUrl(boardId);
  if (!postId) return `/board/${b}`;
  return `/board/${b}/${encodeIdForUrl(postId)}`;
}
